// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";

export interface SignInPageLayoutStyles {
    container?: InjectableCss;
    guestCheckoutPageTitle?: TypographyProps;
}

export const signInPageLayout: SignInPageLayoutStyles = {};

const mapStateToProps = (state: ApplicationState) => {
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromCheckout = returnUrl === shippingPageUrl || returnUrl === onePageCheckoutPageUrl;
    const { allowGuestCheckout } = getSettingsCollection(state).accountSettings;

    return {
        allowGuestCheckout:
            allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromCheckout,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const styles = signInPageLayout;
const SignInPageLayout: FC<Props> = ({ id, allowGuestCheckout }) => {
    const { isEditing } = useShellContext();
    const showGuestCheckout = allowGuestCheckout || isEditing;

    return (
        <>
            <Typography {...styles.guestCheckoutPageTitle}>
                {allowGuestCheckout ? translate("Checkout") : translate("Sign In")}
            </Typography>
            <StyledWrapper
                {...styles.container}
                className={showGuestCheckout ? "checkout-guest-container" : undefined}
                data-test-selector="SignInPageLayout"
            >
                <StyledWrapper className="signInForm">
                    <XngZone contentId={id} zoneName="Content00" hint="SignInExistingAccount" />
                </StyledWrapper>
                {showGuestCheckout && (
                    <>
                        <StyledWrapper className="line-seperator seperator1" />
                        <StyledWrapper className="guestCheckout">
                            <XngZone contentId={id} zoneName="Content02" hint="CheckoutAsGuest" />
                        </StyledWrapper>
                        <StyledWrapper
                            className="line-seperator seperator2"
                            data-test-selector="checkout-guest-seperator"
                        />
                    </>
                )}
                <StyledWrapper className="createAccount">
                    <XngZone contentId={id} zoneName="Content01" hint="CreateAccount" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(SignInPageLayout),
    definition: {
        group: "Layout" as any,
        displayName: "SignIn page layout",
        allowedContexts: ["SignInPage"],
    },
};

export default widgetModule;
